import React, { useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import hoistNonReactStatics from 'hoist-non-react-statics';

import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { hostSystemScenario } from '@bingads-webui-campaign-react/smart-component-util';
import {
  domChildren as domChildrenProps,
  serverContext as serverContextProps,
} from '@bingads-webui-campaign-react/props-mca';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  SmartPageService,
  ImageService,
} from '@bingads-webui-campaign-react/smart-page-data-service';
import { TemplateService } from '@bingads-webui-campaign-react/smart-page-template';
import { getFacebookAppId } from '@bingads-webui-campaign-react/smart-page-util';
import { useIsM365PremiumUser } from '@bingads-webui-campaign-react/up-data';
import { useSmartPageServiceContext } from '@bingads-webui-campaign-react/smart-page-data';

export const DataServiceContext = React.createContext({
  CurrentUser: {},
  CurrentAccount: {},
  CurrentCustomer: {},
  CurrentLocale: '',
  smartPageService: {},
  imageService: {},
  templateService: {},
  customerContext: {},
  accountContext: {},
  campaignContext: {},
  tagContext: {},
  navigationContext: {},
  Permissions: {},
  CCUIPilots: {},
  DynamicPermissions: {},
  Urls: {},
  Omniture: {},
  PageTrackingId: '',
});

export const DataServiceContextProvider = (props) => {
  const {
    serverContext,
    i18n,
    // extendProps,
    overrideProps,
  } = props;

  const isM365PremiumUser = useIsM365PremiumUser();

  const fbAppId = getFacebookAppId(document.domain);

  const permissions = useMemo(() => ({
    ...serverContext.Permissions,
    IsM365PremiumUser: isM365PremiumUser,
  }), [isM365PremiumUser, serverContext.Permissions]);

  const dataServiceContext = useMemo(() => ({
    CurrentUser: serverContext.CurrentUser,
    CurrentAccount: serverContext.CurrentAccount,
    CurrentCustomer: serverContext.CurrentCustomer,
    CurrentLocale: serverContext.CurrentLocale,
    Permissions: permissions,
    CCUIPilots: serverContext.CCUIPilots,
    DynamicPermissions: serverContext.DynamicPermissions,
    smartPageService: SmartPageService({ serverContext }),
    imageService: ImageService({ serverContext, i18n }),
    templateService: TemplateService({ i18n }),
    hostSystemScenario: hostSystemScenario.DMC,
    tagContext: serverContext.tagContext,
    customerContext: serverContext.customerContext,
    accountContext: serverContext.accountContext,
    campaignContext: serverContext.campaignContext,
    navigationContext: serverContext.navigationContext,
    fbAppId,
    Urls: serverContext.Urls,
    Omniture: serverContext.Omniture,
    PageTrackingId: serverContext.PageTrackingId,
    ...overrideProps,
  }), [fbAppId, i18n, overrideProps, permissions, serverContext]);

  return (
    <DataServiceContext.Provider value={dataServiceContext}>
      {props.children}
    </DataServiceContext.Provider>
  );
};

DataServiceContextProvider.propTypes = {
  serverContext: serverContextProps.isRequired,
  children: domChildrenProps.isRequired,
  i18n: PropTypes.shape({}),
  extendProps: PropTypes.shape({}),
  overrideProps: PropTypes.shape({}),
};

DataServiceContextProvider.defaultProps = {
  extendProps: {},
  overrideProps: {},
  i18n: {
    getString: key => key,
  },
};

/**
 * Second option for injecting DataService Context for Component
 * @param {Component} WrappedComponent Wrapped Component
 * @returns {Component} Component with DataServiceContext
 */
export const withDataServiceContext = (WrappedComponent) => {
  const HOC = (props) => {
    const i18n = useContext(I18nContext);
    const serverContext = useSmartPageServiceContext();

    return (
      <DataServiceContextProvider serverContext={serverContext} i18n={i18n}>
        <WrappedComponent {...props} />
      </DataServiceContextProvider>
    );
  };

  HOC.displayName = `withDataServiceContext(${getDisplayName(WrappedComponent)})`;

  hoistNonReactStatics(HOC, WrappedComponent);

  return HOC;
};
